import { useEffect, useState } from "react";
import { Popover } from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { tr } from "date-fns/locale";
import TuneIcon from "@mui/icons-material/Tune";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useIsDark } from "../../hatim/soft/SoftKit";
import { formatShortDate, localDateString, parseLocalDate, startOfMonth } from "../../../lib/dateUtils";
import { PERIOD_OPTIONS, periodRange } from "../../../lib/periodRange";

function presetThisMonth() {
  const now = new Date();
  return { start: localDateString(startOfMonth(now)), end: localDateString(now) };
}

// Mobildeki dönemlerle aynı: Bugün, Dün, Bu hafta, Geçen hafta, Bu ay, Geçen ay.
// key dönem anahtarı (önceki döneme göre kıyasta kullanılır).
const PRESETS = PERIOD_OPTIONS.map((option) => ({
  key: option.key,
  label: option.label,
  build: () => {
    const r = periodRange(option.key);
    return { start: r.startDate, end: r.endDate };
  },
}));

export function matchingPreset(range) {
  return PRESETS.find((p) => {
    const preset = p.build();
    return preset.start === range?.start && preset.end === range?.end;
  });
}

/**
 * İlerleme sekmesinin tarih filtresi.
 * Tek bir düğme seçili aralığı gösteriyor; açılan panelde ya hazır bir aralık
 * seçiliyor ya da tarihler elle giriliyor — ikisi de aynı değeri yazıyor,
 * elle tarih girilince hazır aralık vurgusu kendiliğinden düşüyor.
 */
export default function ProgressRangeBar({ value, onChange }) {
  const { t } = useScriptMode();
  const dark = useIsDark();
  const [anchor, setAnchor] = useState(null);
  // Takvimde hangi ucun düzenlendiği: başlangıç seçilince otomatik bitişe geçer.
  const [editing, setEditing] = useState("start");
  const [start, setStart] = useState(value?.start || presetThisMonth().start);
  const [end, setEnd] = useState(value?.end || presetThisMonth().end);

  useEffect(() => {
    if (value?.start) setStart(value.start);
    if (value?.end) setEnd(value.end);
  }, [value?.start, value?.end]);

  const invalid = Boolean(start && end && start > end);
  const active = matchingPreset({ start, end });

  const apply = (nextStart, nextEnd) => {
    setStart(nextStart);
    setEnd(nextEnd);
    if (!(nextStart && nextEnd && nextStart > nextEnd) && onChange) {
      onChange({ start: nextStart, end: nextEnd });
    }
  };

  const label = active
    ? t(active.label)
    : `${formatShortDate(parseLocalDate(start))} – ${formatShortDate(parseLocalDate(end))}`;

  return (
    <>
      <button
        type="button"
        data-range-trigger
        onClick={(e) => setAnchor(e.currentTarget)}
        className="inline-flex items-center gap-2.5 h-10 pl-3 pr-4 rounded-xl border border-terakkiBorder bg-terakkiSurface hover:border-terakkiPrimary transition-colors dark:bg-white/[0.06] dark:border-white/[0.14] dark:hover:border-white/30"
      >
        <TuneIcon fontSize="small" className="text-terakkiPrimaryDark dark:text-[#8FC9CA]" aria-hidden />
        <span className="flex flex-col items-start leading-tight">
          <span className="text-[10px] uppercase tracking-wide text-terakkiNeutral dark:text-white/70">
            {t("manage_stats_range_label")}
          </span>
          <span className="text-sm font-SemiBold text-terakkiInk dark:text-white">{label}</span>
        </span>
      </button>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { borderRadius: 3, mt: 1, width: 360 } } }}
      >
        <div className="p-4 flex flex-col gap-4">
          <div>
            <div className="text-xs font-SemiBold uppercase tracking-wide text-terakkiNeutral mb-2 dark:text-white/70">
              {t("manage_stats_presets_label")}
            </div>
            <div className="grid grid-cols-3 rounded-lg border border-terakkiBorder overflow-hidden dark:border-white/15">
              {PRESETS.map((preset) => {
                const isActive = active?.key === preset.key;
                return (
                  <button
                    key={preset.key}
                    type="button"
                    data-preset={preset.key}
                    aria-pressed={isActive}
                    onClick={() => {
                      const p = preset.build();
                      apply(p.start, p.end);
                    }}
                    className={[
                      "px-2 h-10 text-xs font-medium border-r border-b border-terakkiBorder [&:nth-child(3n)]:border-r-0 [&:nth-last-child(-n+3)]:border-b-0 transition-colors dark:border-white/15",
                      isActive
                        ? "bg-terakkiPrimaryDark text-white"
                        : "bg-terakkiSurface text-terakkiInk hover:bg-terakkiBrandCloud dark:bg-transparent dark:text-white dark:hover:bg-white/5",
                    ].join(" ")}
                  >
                    {t(preset.label)}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-xs font-SemiBold uppercase tracking-wide text-terakkiNeutral mb-2 dark:text-white/70">
              {t("manage_stats_custom_range_label")}
            </div>

            {/* Tarayıcının kendi tarih penceresi yerine tema renkleriyle çizilen
                takvim; hangi ucun seçildiğini üstteki iki sekme belirliyor. */}
            <div className="grid grid-cols-2 rounded-lg border border-terakkiBorder overflow-hidden mb-3 dark:border-white/15">
              {[
                { key: "start", label: t("manage_stats_range_start"), value: start },
                { key: "end", label: t("manage_stats_range_end"), value: end },
              ].map((edge) => (
                <button
                  key={edge.key}
                  type="button"
                  data-edge={edge.key}
                  aria-pressed={editing === edge.key}
                  onClick={() => setEditing(edge.key)}
                  className={[
                    "px-3 py-2 text-left border-r border-terakkiBorder last:border-r-0 transition-colors dark:border-white/15",
                    editing === edge.key
                      ? "bg-terakkiBrandCloud dark:bg-white/10"
                      : "bg-terakkiSurface hover:bg-terakkiSurfaceAlt dark:bg-transparent dark:hover:bg-white/5",
                  ].join(" ")}
                >
                  <span className="block text-[10px] uppercase tracking-wide text-terakkiNeutral dark:text-white/70">
                    {edge.label}
                  </span>
                  <span className="block text-sm font-SemiBold text-terakkiInk dark:text-white">
                    {formatShortDate(parseLocalDate(edge.value))}
                  </span>
                </button>
              ))}
            </div>

            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
              <DateCalendar
                value={parseLocalDate(editing === "start" ? start : end)}
                maxDate={editing === "start" ? parseLocalDate(end) : undefined}
                minDate={editing === "end" ? parseLocalDate(start) : undefined}
                onChange={(date) => {
                  if (!date) return;
                  const iso = localDateString(date);
                  if (editing === "start") {
                    apply(iso, end);
                    setEditing("end");
                  } else {
                    apply(start, iso);
                  }
                }}
                sx={{
                  width: "100%",
                  m: 0,
                  "& .MuiPickersDay-root.Mui-selected": {
                    backgroundColor: dark ? "#FFFFFF" : "#006A71",
                    color: dark ? "#006A71" : "#FFFFFF",
                    "&:hover": { backgroundColor: dark ? "#E6F4F4" : "#00565C" },
                  },
                  "& .MuiPickersDay-root:focus.Mui-selected": { backgroundColor: dark ? "#FFFFFF" : "#006A71" },
                  "& .MuiPickersCalendarHeader-label": { fontWeight: 700, color: dark ? "#FFFFFF" : "#123B3D" },
                }}
              />
            </LocalizationProvider>
          </div>

          {invalid && (
            <p className="text-xs text-terakkiError dark:text-[#F2A59C]">{t("manage_stats_invalid_range")}</p>
          )}

          <p className="flex items-center gap-1.5 text-[11px] text-terakkiNeutral dark:text-white/70">
            <CalendarMonthIcon sx={{ fontSize: 14 }} aria-hidden />
            {t("manage_stats_range_hint")}
          </p>
        </div>
      </Popover>
    </>
  );
}
